/**
 * Phone number utility functions
 */

/**
 * Formats a phone number to ensure it includes the Honduras country code (+504)
 * if it doesn't already have a country code
 *
 * @param phoneNumber - Raw phone number from API
 * @returns Formatted phone number with country code
 */
export function formatPhoneWithCountryCode(phoneNumber: string): string {
  if (!phoneNumber) return '';

  // Remove any whitespace and special characters except + and digits
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');

  // If phone already starts with +, assume it has a country code
  if (cleanPhone.startsWith('+')) {
    return cleanPhone;
  }

  // If phone starts with 504, assume it's Honduras but missing the +
  if (cleanPhone.startsWith('504')) {
    return `+${cleanPhone}`;
  }

  // If it's an 8-digit number (typical Honduras mobile format), add +504
  if (cleanPhone.length === 8 && /^\d{8}$/.test(cleanPhone)) {
    return `+504${cleanPhone}`;
  }

  // If it's any other format without country code, assume Honduras
  if (cleanPhone.length > 0 && !cleanPhone.startsWith('+')) {
    return `+504${cleanPhone}`;
  }

  return cleanPhone;
}

/**
 * Validates if a phone number appears to be a valid Honduras phone number
 *
 * @param phoneNumber - Phone number to validate
 * @returns True if valid Honduras format
 */
export function isValidHondurasPhone(phoneNumber: string): boolean {
  if (!phoneNumber) return false;

  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');

  // Valid formats:
  // +50412345678 (8 digits after +504)
  // +50498765432 (8 digits after +504)
  const hondurasPhoneRegex = /^\+504\d{8}$/;

  return hondurasPhoneRegex.test(cleanPhone);
}

/**
 * Extracts just the local number part (without country code) from a phone number
 *
 * @param phoneNumber - Full phone number with country code
 * @returns Local number without country code
 */
export function extractLocalNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';

  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');

  // If it starts with +504, return everything after that
  if (cleanPhone.startsWith('+504')) {
    return cleanPhone.substring(4);
  }

  // If it starts with 504 (no +), return everything after that
  if (cleanPhone.startsWith('504')) {
    return cleanPhone.substring(3);
  }

  // Otherwise return as-is
  return cleanPhone;
}
